/**
 * SONE — keymap.
 *
 * The keys that decide whether a block editor feels right. Four of them do most
 * of the work and each has a non-obvious correct behaviour:
 *
 *   Enter        splits the block, and a *second* Enter in an empty list item
 *                lifts it out rather than creating another empty item. Without
 *                that, leaving a list means reaching for the mouse.
 *   Tab          indents by nesting inside the previous sibling. Only valid
 *                when there *is* a previous sibling of a container type —
 *                indenting the first item of a list has no meaning.
 *   Shift-Tab    outdents by lifting into the grandparent.
 *   Backspace    at the start of a block converts it to a paragraph before
 *                merging, so backspacing out of a heading gives plain text
 *                rather than swallowing the previous block.
 */
import { BLOCK_ATTRS } from '@sone/core';
import { redo, undo } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap, chainCommands, createParagraphNear, deleteSelection, joinBackward, liftEmptyBlock, newlineInCode, selectNodeBackward, setBlockType, splitBlock, toggleMark, } from 'prosemirror-commands';
import { Fragment, Slice } from 'prosemirror-model';
import { duplicateBlockSubtree, indentBlockSubtree, moveBlockDown, moveBlockUp, outdentBlockSubtree, } from './blockOps.js';
import { toggleCollapsed } from './collapse.js';
import { canLink, selectLink } from './links.js';
import { readIndent, schema, writeIndent } from './schema.js';
/**
 * The block containing the selection head, with its position and depth.
 *
 * Walks outwards from the selection rather than assuming depth 1: a block may
 * be nested inside a container, and the innermost node carrying block
 * attributes is the one the user is editing.
 */
function currentBlock(state) {
    const { $from } = state.selection;
    for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        if (node.type.spec.attrs && BLOCK_ATTRS.id in node.type.spec.attrs) {
            return { node, depth, pos: $from.before(depth) };
        }
    }
    return null;
}
/**
 * Enter in an empty container block lifts it out instead of splitting.
 *
 * The behaviour people expect from every list they have ever used: Enter on an
 * empty bullet ends the list. Without it, the only way out is Backspace or the
 * mouse.
 */
const exitEmptyContainer = (state, dispatch) => {
    const block = currentBlock(state);
    if (!block)
        return false;
    if (block.node.type.name === 'paragraph')
        return false;
    if (block.node.textContent.length > 0)
        return false;
    const indent = readIndent(block.node.attrs);
    // Indented: step out one level first. Repeated Enter then walks out of a
    // nested list one level at a time, which is what people expect.
    if (indent > 0) {
        if (dispatch) {
            dispatch(state.tr
                .setNodeAttribute(block.pos, BLOCK_ATTRS.indent, writeIndent(indent - 1))
                .scrollIntoView());
        }
        return true;
    }
    const paragraph = schema.nodes['paragraph'];
    if (!paragraph)
        return false;
    if (dispatch) {
        dispatch(state.tr
            .setNodeMarkup(block.pos, paragraph, {
            // The id is kept: this is the same block changing type, and a new id
            // would orphan every reference to it.
            [BLOCK_ATTRS.id]: block.node.attrs[BLOCK_ATTRS.id],
            [BLOCK_ATTRS.props]: null,
            [BLOCK_ATTRS.indent]: null,
        })
            .scrollIntoView());
    }
    return true;
};
/**
 * Indent and outdent, taking the block's indented children with it.
 *
 * Delegated to blockOps rather than implemented here. The version that lived in
 * this file shifted only the selected block, which turned its children into its
 * siblings — quietly, with nothing throwing and nothing looking wrong. That
 * shipped. See src/blockOps.ts.
 */
const indentBlock = indentBlockSubtree;
const outdentBlock = outdentBlockSubtree;
/**
 * Block types that continue when Enter is pressed.
 *
 * A heading is deliberately absent: Enter after a heading should start body
 * text, not another heading, which is what every editor does and what people
 * expect. Code has its own handler.
 */
const CONTINUING = new Set([
    'bulletList',
    'numberedList',
    'todo',
    'quote',
    'callout',
]);
/**
 * Attributes that must not be inherited by the next item.
 *
 * A new to-do after a completed one must not arrive already ticked, and a new
 * toggle must not arrive collapsed. Both would be actively wrong rather than
 * merely surprising.
 */
const RESET_ON_SPLIT = {
    checked: false,
    collapsed: false,
};
/**
 * Enter inside a list, to-do, quote or callout continues with another of the
 * same, at the same indent.
 *
 * The id is cleared rather than copied: block ids are globally unique because
 * the projection keys on them, so two blocks sharing one would have the second
 * overwrite the first. The blockIds plugin assigns a fresh one on the next
 * transaction.
 */
const continueSameBlock = (state, dispatch) => {
    const { $from, empty } = state.selection;
    if (!empty)
        return false;
    for (let depth = $from.depth; depth > 0; depth--) {
        const node = $from.node(depth);
        const spec = node.type.spec.attrs;
        if (!spec || !(BLOCK_ATTRS.id in spec))
            continue;
        if (!CONTINUING.has(node.type.name))
            return false;
        // An empty item is handled by exitEmptyContainer, which runs first: Enter
        // there leaves the list rather than adding another empty row.
        if (node.content.size === 0)
            return false;
        if (dispatch) {
            const attrs = { ...node.attrs, [BLOCK_ATTRS.id]: null };
            for (const [key, value] of Object.entries(RESET_ON_SPLIT)) {
                if (key in node.attrs)
                    attrs[key] = value;
            }
            dispatch(state.tr
                .split($from.pos, 1, [{ type: node.type, attrs }])
                .scrollIntoView());
        }
        return true;
    }
    return false;
};
/**
 * Backspace at the start of a non-paragraph block turns it into a paragraph.
 *
 * Tried before the default join, so backspacing at the start of a heading gives
 * plain text rather than merging it into whatever came before. Merging is still
 * what a second Backspace does, which matches every editor people are used to.
 */
const paragraphBeforeJoin = (state, dispatch) => {
    const { $from, empty } = state.selection;
    if (!empty || $from.parentOffset !== 0)
        return false;
    const block = currentBlock(state);
    if (!block)
        return false;
    const indent = readIndent(block.node.attrs);
    // Indented blocks outdent first, so Backspace at the start of a nested item
    // walks it out rather than merging it into its parent's text.
    if (indent > 0) {
        if (dispatch) {
            dispatch(state.tr.setNodeAttribute(block.pos, BLOCK_ATTRS.indent, writeIndent(indent - 1)));
        }
        return true;
    }
    if (block.node.type.name === 'paragraph')
        return false;
    const paragraph = schema.nodes['paragraph'];
    if (!paragraph)
        return false;
    if (dispatch) {
        dispatch(state.tr.setNodeMarkup(block.pos, paragraph, {
            [BLOCK_ATTRS.id]: block.node.attrs[BLOCK_ATTRS.id],
            [BLOCK_ATTRS.props]: null,
            [BLOCK_ATTRS.indent]: null,
        }));
    }
    return true;
};
/**
 * Every top-level block the selection touches that can hold writing (ADR-0165).
 *
 * Top-level, and it does not descend: a paragraph inside a table cell belongs
 * to the table, and turning the four cells somebody dragged across into bullets
 * is not what „diese Zeilen" meant. A block that holds no inline content at all
 * — a divider, an image, the table itself — is stepped over rather than
 * refused, because failing the whole conversion over something in the middle of
 * the selection is a command that does nothing for a reason nobody can see.
 *
 * Exported because the block menu has to ask the same question — whether this
 * menu is about one block or about the four somebody dragged across — and the
 * answer has to be the same one the command will act on. `selectedBlockRange`
 * cannot say it: that range is a block **and its indented children**, so it is
 * larger than one for a bullet with sub-bullets and says nothing about what is
 * selected.
 */
export function blocksInSelection(state) {
    const { from, to } = state.selection;
    const found = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
        if (!node.isBlock || !node.type.spec.group?.includes('block'))
            return true;
        if (node.inlineContent)
            found.push({ node, pos });
        return false;
    });
    return found;
}
/**
 * Toggle a block between a type and paragraph. Used by menus and shortcuts.
 *
 * **Every block the selection covers**, not the one the caret happens to be in
 * (ADR-0165). Reported with four lines selected: *„Wenn ich mehrere Zeilen Text
 * markiere würde ich diese gerne auch im Verbund umwandeln können."* Its
 * sibling `setBlockStyle` has always walked the selection, so the block menu was
 * answering its two questions about the same four lines at two different
 * scopes.
 *
 * A selection inside one block keeps the old path exactly, `currentBlock` and
 * all — that one climbs out of a table cell to the block it is in, which the
 * walk above deliberately does not do.
 */
export function toggleBlockType(type, attrs = {}) {
    return (state, dispatch) => {
        const paragraph = schema.nodes['paragraph'];
        if (!paragraph)
            return false;
        const spanned = blocksInSelection(state);
        const isThisType = (node) => node.type === type &&
            Object.entries(attrs).every(([key, value]) => node.attrs[key] === value);
        /*
         * Made the same, rather than each one flipped.
         *
         * Two bullets and two paragraphs, asked to be a bullet list, become four
         * bullets. Flipping each block on its own would swap the two kinds and
         * leave the selection exactly as mixed as it was — pressing "bullet list"
         * and getting a *different* mixture is the one outcome nobody wants. It
         * turns back only when every selected block is already that type.
         */
        if (spanned.length > 1) {
            // `.every(one => …one.node)`, not `.every(isThisType)`: these are
            // `{node, pos}` pairs, and handing the pair to a predicate about a node
            // reads `undefined === type` — false for everything, forever, which made
            // the toggle a one-way trip.
            const target = spanned.every((one) => isThisType(one.node)) ? paragraph : type;
            if (dispatch) {
                const tr = state.tr;
                for (const one of spanned) {
                    const preserved = {
                        [BLOCK_ATTRS.id]: one.node.attrs[BLOCK_ATTRS.id],
                        [BLOCK_ATTRS.props]: null,
                        [BLOCK_ATTRS.indent]: one.node.attrs[BLOCK_ATTRS.indent],
                    };
                    // The positions stay valid across the loop: changing a node's type
                    // and attributes leaves its content, and therefore its size, alone.
                    tr.setNodeMarkup(one.pos, target, target === paragraph ? preserved : { ...attrs, ...preserved });
                }
                dispatch(tr.scrollIntoView());
            }
            return true;
        }
        const block = currentBlock(state);
        if (!block)
            return false;
        const alreadyThisType = isThisType(block.node);
        const target = alreadyThisType ? paragraph : type;
        // Indent is preserved across a type change: converting an indented bullet
        // to a heading should keep it where it sits, not jump it to the margin.
        const preserved = {
            [BLOCK_ATTRS.id]: block.node.attrs[BLOCK_ATTRS.id],
            [BLOCK_ATTRS.props]: null,
            [BLOCK_ATTRS.indent]: block.node.attrs[BLOCK_ATTRS.indent],
        };
        const nextAttrs = alreadyThisType ? preserved : { ...attrs, ...preserved };
        if (dispatch) {
            dispatch(state.tr.setNodeMarkup(block.pos, target, nextAttrs).scrollIntoView());
        }
        return true;
    };
}
/** Toggle a todo's checked state without moving the caret. */
export const toggleTodo = (state, dispatch) => {
    const block = currentBlock(state);
    if (!block || block.node.type.name !== 'todo')
        return false;
    if (dispatch) {
        dispatch(state.tr.setNodeAttribute(block.pos, 'checked', block.node.attrs['checked'] !== true));
    }
    return true;
};
/** Insert a divider followed by an empty paragraph to type into. */
export const insertDivider = (state, dispatch) => {
    const divider = schema.nodes['divider'];
    const paragraph = schema.nodes['paragraph'];
    if (!divider || !paragraph)
        return false;
    if (dispatch) {
        const tr = state.tr.replaceSelection(new Slice(Fragment.from([divider.create(), paragraph.create()]), 0, 0));
        dispatch(tr.scrollIntoView());
    }
    return true;
};
/** Exposed for tests and for menu items that need the same behaviour. */
export const indentCommand = () => indentBlock;
export const outdentCommand = () => outdentBlock;
export function soneKeymap() {
    const heading = schema.nodes['heading'];
    const bullet = schema.nodes['bulletList'];
    const numbered = schema.nodes['numberedList'];
    const todo = schema.nodes['todo'];
    const code = schema.nodes['code'];
    const quote = schema.nodes['quote'];
    const bindings = {
        // Order matters: exiting an empty container is tried before splitting, or
        // Enter on an empty bullet would create another empty bullet forever.
        Enter: chainCommands(newlineInCode, exitEmptyContainer, 
        // Before splitBlock, which is why this exists at all: ProseMirror's
        // splitBlock creates a block of the parent's *default* type, so Enter at
        // the end of a to-do produced a paragraph. Pressing Enter to add the next
        // item in a list is the single most common keystroke in a notes app.
        continueSameBlock, liftEmptyBlock, splitBlock),
        // A hard line break inside a block, for the cases where a new block is
        // not wanted.
        'Shift-Enter': (state, dispatch) => {
            if (dispatch) {
                dispatch(state.tr.insertText('\n').scrollIntoView());
            }
            return true;
        },
        Tab: indentBlock,
        'Shift-Tab': outdentBlock,
        Backspace: chainCommands(deleteSelection, paragraphBeforeJoin, joinBackward, selectNodeBackward),
        'Mod-z': undo,
        'Mod-y': redo,
        'Shift-Mod-z': redo,
        'Mod-b': toggleMark(schema.marks['strong']),
        'Mod-i': toggleMark(schema.marks['em']),
        'Mod-Shift-x': toggleMark(schema.marks['strikethrough']),
        'Mod-e': toggleMark(schema.marks['inlineCode']),
        // Mod-K is the link shortcut everywhere, so it must not fall through to the
        // browser's search bar. It only selects the link — the interface opens an
        // editor for it, because a URL cannot be typed into a keymap.
        'Mod-k': (state, dispatch) => {
            if (!canLink(state))
                return false;
            // Returns true even without dispatching, so the key is consumed and the
            // interface can react to the selection it leaves behind.
            selectLink(state, dispatch);
            return true;
        },
        'Mod-Enter': toggleTodo,
        'Mod-Shift-Minus': insertDivider,
        // Collapse or expand the toggle the caret is in. Mod-. because it is free
        // on every platform and adjacent to nothing destructive.
        'Mod-.': toggleCollapsed,
        // Reordering. Alt rather than Mod, because Mod-Shift-Up is a text selection
        // shortcut on every platform and taking it away would be worse than not
        // offering this.
        'Alt-Shift-ArrowUp': moveBlockUp,
        'Alt-Shift-ArrowDown': moveBlockDown,
        'Mod-d': duplicateBlockSubtree,
    };
    if (heading) {
        // Mod-Alt-1..3 rather than Mod-1..3, which browsers use for tab switching.
        for (const level of [1, 2, 3, 4, 5, 6]) {
            bindings[`Mod-Alt-${level}`] = toggleBlockType(heading, { level });
        }
    }
    if (bullet)
        bindings['Mod-Shift-8'] = toggleBlockType(bullet);
    if (numbered)
        bindings['Mod-Shift-7'] = toggleBlockType(numbered);
    if (todo)
        bindings['Mod-Shift-9'] = toggleBlockType(todo);
    if (quote)
        bindings['Mod-Shift-b'] = toggleBlockType(quote);
    if (code)
        bindings['Mod-Shift-c'] = toggleBlockType(code);
    const paragraph = schema.nodes['paragraph'];
    if (paragraph) {
        bindings['Mod-Alt-0'] = setBlockType(paragraph);
    }
    return [
        keymap(bindings),
        // Base keymap last, so the bindings above win where they overlap.
        keymap(baseKeymap),
    ];
}
export { createParagraphNear };
//# sourceMappingURL=keymap.js.map